// pages/TravelExpense/TravelExpenseDetail.js
//
// Shows one ExpenseApproval (trip) with its expense breakdown and the
// approval log. Manager users see Approve / Reject buttons.
//
// Backend:
//   GET  /api/TravelExpense/{id}
//   POST /api/TravelExpense/{id}/approve   { comment }
//   POST /api/TravelExpense/{id}/reject    { comment }   (comment required)
//   POST /api/TravelExpense/{id}/void

import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Row, Col, Button, Input, Label,
    Spinner, Badge, Alert } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { toCamel } from "./travelCase";
import TravelWorkspaceTabs from "./TravelWorkspaceTabs";   // Go-live parity (2026-09-24): #2/#3/#4
import { TravelStatusBadge, MAX_COMMENT } from "./travelStatus";
import { printTea, printFullReport } from "./travelPrint";

const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const fmtDt   = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString();    } catch { return "—"; } };
const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

const TravelExpenseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [detail, setDetail] = useState(null);   // whole camelCased GET /{id} response
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const reload = () => {
        setLoading(true); setError(null);
        api.get(`TravelExpense/${id}`)
            .then(({ data }) => {
                // Go-live parity (2026-09-24): camelCase response (was data.Trip / data.Log only).
                setDetail(toCamel(data) ?? null);
            })
            .catch((e) => setError(e?.response?.data?.error || e?.message || "Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(reload, [id]);  // eslint-disable-line

    const act = async (kind) => {
        setActionError(null);
        // Go-live parity (2026-09-24): #16 — a denial needs a reason (legacy:
        // "Because you are denying this request, a comment is required. This
        // comment will be shown to the employee who had made the request.").
        if (kind === "reject" && !comment.trim()) {
            setActionError("A comment is required to deny a request. It will be shown to the employee who made the request.");
            return;
        }
        if (kind === "reject" && !window.confirm("Please confirm your denial for this travel request.")) return;
        if (kind === "void" && !window.confirm("Please confirm you wish to void this request.")) return;
        setBusy(true);
        try {
            await api.post(`TravelExpense/${id}/${kind}`, kind === "void" ? {} : { comment: comment.trim() });
            setComment("");
            reload();
        } catch (e) {
            setActionError(e?.response?.data?.error || e?.message || `${kind} failed`);
        } finally {
            setBusy(false);
        }
    };

    if (loading) return (<div className="page-content"><Container fluid><div className="text-center py-5"><Spinner color="primary" /></div></Container></div>);
    if (error)   return (<div className="page-content"><Container fluid><Alert color="danger">{error}</Alert></Container></div>);
    const trip = detail?.trip;
    if (!trip)   return (<div className="page-content"><Container fluid><Alert color="warning">Trip not found.</Alert></Container></div>);

    const log = Array.isArray(detail.log) ? detail.log : [];
    const destinations = Array.isArray(detail.destinations) ? detail.destinations : [];
    const travelers = Array.isArray(detail.travelers) ? detail.travelers : [];
    const viewer = detail.viewer || {};

    // Go-live parity (2026-09-24): #15/#17 — the server decides who may approve
    // (approval module + scope + Status 2), not the client's manager flag.
    const isApprovable = !!viewer.canDecide;
    // #7/#9 — the owner reopens a Draft (1) or a Denied (4) request in the
    // edit form; a request awaiting approval (2) is read-only (#10).
    const canEdit = !!viewer.canEdit;
    const canVoid = !!viewer.canVoid;       // #11
    const canPrint = !!viewer.canPrint;     // #20

    // #27 — legacy status 2 reads "Awaiting approval of <supervisor>".
    const statusLabel = trip.status === 2 && detail.approverName
        ? `Awaiting approval of ${detail.approverName}`
        : detail.statusLabel;

    const onPrintTea = () => { if (!printTea(detail)) setActionError("Allow pop-ups for this site to print."); };
    const onPrintFull = () => { printFullReport(trip.id, detail).then((ok) => { if (!ok) setActionError("Allow pop-ups for this site to print."); }); };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    actions={(
                        <div className="d-flex gap-2 flex-wrap">
                            {canPrint && (
                                <Button color="secondary" outline onClick={onPrintTea}>
                                    <i className="ri-printer-line align-bottom me-1" />Print TEA
                                </Button>
                            )}
                            {trip.status >= 3 && trip.status !== 4 && trip.status !== 8 && (
                                <Button color="secondary" outline onClick={onPrintFull}>
                                    <i className="ri-file-list-3-line align-bottom me-1" />Print Entire Report
                                </Button>
                            )}
                            {canVoid && (
                                <Button color="danger" outline disabled={busy} onClick={() => act("void")}>Void</Button>
                            )}
                            {canEdit && (
                                <Button color="primary" onClick={() => navigate(`/travel-expense/${trip.id}/edit`)}>
                                    {trip.status === 4 ? "Edit / Resubmit" : "Edit / Submit"}
                                </Button>
                            )}
                        </div>
                    )}
                    title={trip.nameOfTrip ?? `Trip #${trip.id}`}
                    breadcrumb={[{ label: "Travel Expense", to: "/workflows?wf=travel" }, { label: trip.nameOfTrip ?? `Trip #${trip.id}` }]}
                    subtitle={<>
                        <TravelStatusBadge status={trip.status} label={statusLabel} />{" "}
                        <span style={{ color: "rgba(255, 255, 255, 0.85)" }}>Trip #{trip.id} · prepared {fmtDate(trip.submitDate)}
                            {detail.requesterName ? ` by ${detail.requesterName}` : ""}</span>
                    </>}
                    breadcrumbItems={[
                        { label: "Travel Expense", href: "/travel-expense" },
                        { label: `Trip #${trip.id}` },
                    ]}
                />
                {actionError && <Alert color="danger" className="mt-2" toggle={() => setActionError(null)}>{actionError}</Alert>}

                <Row className="g-3 mt-1">
                    <Col xl={8}>
                        <Card>
                            <CardBody>
                                <h5 className="mb-3">Trip details</h5>
                                <Row className="g-2">
                                    <Col md={6}><div className="text-muted small">Proposed By</div><div>{detail.requesterName || trip.empid}</div></Col>
                                    <Col md={3}><div className="text-muted small">Leave Home</div><div>{fmtDate(trip.leaveHomeDate)}</div></Col>
                                    <Col md={3}><div className="text-muted small">Return Home</div><div>{fmtDate(trip.returnHomeDate)}</div></Col>
                                    <Col md={12}><div className="text-muted small">Primary Objective</div><div style={{ whiteSpace: "pre-wrap" }}>{trip.primaryObjective || "—"}</div></Col>
                                    {/* Go-live parity (2026-09-24): #14 — who is travelling. */}
                                    <Col md={12}>
                                        <div className="text-muted small">Travelers</div>
                                        <div>
                                            {travelers.length === 0 ? "—" : travelers.map((t) => (
                                                <Badge key={t.empid} color="info" pill className="me-1 mb-1">{t.name}</Badge>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Go-live parity (2026-09-24): #14 — where the trip goes. */}
                                <h6 className="mt-4 mb-2">Destinations</h6>
                                {destinations.length === 0 ? (
                                    <div className="text-muted small">No destinations.</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Start</th><th>End</th><th className="text-end">Hours</th><th>Nature of Visit</th>
                                                    <th>Company</th><th>City, State, Country</th><th>Contact</th><th>Goal</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {destinations.map((d) => (
                                                    <tr key={d.id}>
                                                        <td>{fmtDate(d.startDate)}</td>
                                                        <td>{fmtDate(d.endDate)}</td>
                                                        <td className="text-end">{d.numberOfHours}</td>
                                                        <td>{d.natureOfVisit}</td>
                                                        <td>{d.company}</td>
                                                        <td>{d.cityStateCountry}</td>
                                                        <td>{d.contactPerson}</td>
                                                        <td style={{ whiteSpace: "pre-wrap" }}>{d.goal}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <h6 className="mt-4 mb-2">Estimated Expenses</h6>
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        <tr><th>Airfare</th>        <td className="text-end">{fmtMoney(trip.airfare)}</td></tr>
                                        <tr><th>Lodging</th>        <td className="text-end">{fmtMoney(trip.lodging)}</td></tr>
                                        <tr><th>Meals</th>          <td className="text-end">{fmtMoney(trip.meals)}</td></tr>
                                        <tr><th>Entertainment</th>  <td className="text-end">{fmtMoney(trip.entertainment)}</td></tr>
                                        <tr><th>Car Rental</th>     <td className="text-end">{fmtMoney(trip.carRental)}</td></tr>
                                        <tr><th>Personal Car</th>   <td className="text-end">{fmtMoney(trip.personalCar)}</td></tr>
                                        <tr><th>Taxi</th>           <td className="text-end">{fmtMoney(trip.taxi)}</td></tr>
                                        <tr><th>Other</th>          <td className="text-end">{fmtMoney(trip.other)}</td></tr>
                                        <tr className="table-light"><th>Total</th>
                                            <td className="text-end fw-bold">
                                                {fmtMoney((trip.airfare ?? 0) + (trip.lodging ?? 0) + (trip.meals ?? 0)
                                                    + (trip.entertainment ?? 0) + (trip.carRental ?? 0)
                                                    + (trip.personalCar ?? 0) + (trip.taxi ?? 0) + (trip.other ?? 0))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                {trip.otherNote && <div className="text-muted small mt-2"><em>Other note:</em> {trip.otherNote}</div>}
                            </CardBody>
                        </Card>

                        {isApprovable && (
                            <Card className="mt-3">
                                <CardBody>
                                    <h5 className="mb-3">Your decision</h5>
                                    <Label>Comment (required to deny)</Label>
                                    <Input type="textarea" rows={3} value={comment} maxLength={MAX_COMMENT}
                                        onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
                                        disabled={busy} />
                                    <div className="text-muted small text-end">{comment.length}/{MAX_COMMENT}</div>
                                    <div className="mt-2 d-flex gap-2">
                                        <Button color="success" disabled={busy} onClick={() => act("approve")}>
                                            {busy ? <Spinner size="sm" /> : "Approve"}
                                        </Button>
                                        <Button color="danger" outline disabled={busy} onClick={() => act("reject")}>
                                            Reject
                                        </Button>
                                        <Button color="link" onClick={() => navigate("/travel-expense")}>Cancel</Button>
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </Col>

                    <Col xl={4}>
                        <Card>
                            <CardBody>
                                <h5 className="mb-3">Approval history</h5>
                                {log.length === 0 ? (
                                    <div className="text-muted">No approvals or comments yet.</div>
                                ) : (
                                    <ul className="list-unstyled mb-0">
                                        {log.map(l => (
                                            <li key={l.id} className="mb-2 pb-2 border-bottom">
                                                <div>
                                                    <Badge color={l.status === "APPROVED" ? "success" : l.status === "DENIED" ? "danger" : "secondary"} pill>{l.status}</Badge>
                                                    {/* Go-live parity (2026-09-24): #14 — names, not "empId N". */}
                                                    {" "}<small className="text-muted">{l.name || (l.empid ? `empId ${l.empid}` : "")}</small>
                                                </div>
                                                <div className="text-muted small">{fmtDt(l.stamp)}</div>
                                                {l.comment && <div className="mt-1 small">{l.comment}</div>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Go-live parity (2026-09-24): #2/#3/#4 — Expenses, Trip Report and
                    Pre-Arrangements once the trip is approved (renders nothing before). */}
                <TravelWorkspaceTabs tripId={trip.id} />
            </Container>
        </div>
    );
};

export default TravelExpenseDetail;
